import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * Log viewer data — endpointy `/admin/api/v1/logs/*` (komunikační logy z DB pod
 * `/communication`, aplikační in-memory buffer pod `/application`).
 * Komunikační logy jsou keyset/cursor-paginované s volitelným
 * `countTotal`. Port 1:1 z původního Thymeleaf vieweru.
 */
export interface CommLogEntry {
  id: number;
  timestamp: string;
  authServerType: string;
  direction: string | null;
  method: string | null;
  endpoint: string | null;
  statusCode: number | null;
  durationMs: number | null;
  tokenId: string | null;
  traceId: string | null;
  errorMessage: string | null;
}

export interface CommLogsResponse {
  success: boolean;
  logs: CommLogEntry[];
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;
  nextCursorId: number | null;
  nextCursorTimestamp: string | null;
  prevCursorId: number | null;
  prevCursorTimestamp: string | null;
  totalElements?: number;
}

export interface HeaderFilter {
  name: string;
  value: string;
}

export interface CommLogFilter {
  search?: string;
  authServerType?: string;
  /** Čárkou oddělené endpoint patterny (z chipů i ručního vstupu). */
  endpoint?: string;
  minStatus?: string;
  maxStatus?: string;
  /** datetime-local `yyyy-MM-ddTHH:mm` (`:00` se doplní). */
  from?: string;
  to?: string;
  tokenId?: string;
  headerFilters?: HeaderFilter[];
}

/** Keyset cursor — `after*` = starší (dopředu), `before*` = novější (zpět). */
export type CommLogCursor =
  | { afterTimestamp: string; afterId: number }
  | { beforeTimestamp: string; beforeId: number }
  | null;

/** Přednastavené endpoint skupiny (chipy) — statické, mirror LogsViewerWebController. */
export const ENDPOINT_GROUPS: { label: string; filter: string }[] = [
  { label: "Tokens", filter: "/tokens" },
  { label: "Transactions", filter: "/transactions" },
  { label: "Device", filter: "/device-replacement" },
  { label: "License", filter: "/license" },
  { label: "Stats", filter: "/stats" },
  { label: "Maintenance", filter: "/maintenance" },
  { label: "Connector Config", filter: "/connector-config" },
  { label: "Logs", filter: "/logs" },
];

function buildFilterParams(filter: CommLogFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (filter.search) p.set("search", filter.search);
  if (filter.authServerType) p.set("authServerType", filter.authServerType);
  if (filter.endpoint) p.set("endpoint", filter.endpoint);
  if (filter.minStatus) p.set("minStatus", filter.minStatus);
  if (filter.maxStatus) p.set("maxStatus", filter.maxStatus);
  if (filter.from) p.set("from", filter.from + ":00");
  if (filter.to) p.set("to", filter.to + ":00");
  if (filter.tokenId) p.set("tokenId", filter.tokenId);
  const hf = (filter.headerFilters ?? []).filter((h) => h.name.trim() || h.value.trim());
  if (hf.length > 0) p.set("headerFilters", JSON.stringify(hf));
  return p;
}

export function useCommLogs(
  size: number,
  filter: CommLogFilter = {},
  cursor: CommLogCursor = null,
  countTotal = false,
  autoRefreshMs = 0,
) {
  const p = buildFilterParams(filter);
  p.set("size", String(size));
  if (countTotal) p.set("countTotal", "true");
  if (cursor) for (const [k, v] of Object.entries(cursor)) p.set(k, String(v));
  return useQuery({
    queryKey: ["comm-logs", size, p.toString()],
    queryFn: () => apiFetch<CommLogsResponse>(`/admin/api/v1/logs/communication?${p.toString()}`),
    // Logy jsou proud, ne stav — globální staleTime 30 s by po návratu na stránku
    // vracel půl minuty starý snímek, aniž by na server vůbec sáhl (refetchOnWindowFocus
    // je taky vypnutý). Při ověřování, jestli se řádky zapisují, to čte jako by se
    // nedělo nic. Stejné nastavení má useServerStats ze stejného důvodu.
    staleTime: 0,
    refetchInterval: autoRefreshMs > 0 ? autoRefreshMs : false,
  });
}

export interface CommLogDetail extends CommLogEntry {
  requestHeaders: unknown;
  requestData: unknown;
  responseHeaders: unknown;
  responseData: unknown;
  testTraceId: string | null;
}

export function useCommLogDetail(id: number | null) {
  return useQuery({
    queryKey: ["comm-logs", "detail", id],
    queryFn: () => apiFetch<{ success: boolean; log: CommLogDetail }>(`/admin/api/v1/logs/communication/${id}`),
    enabled: id != null,
  });
}

/** Export URL s aktuálními filtry (session cookie, web chain). */
export function commLogExportUrl(format: "json" | "csv", filter: CommLogFilter, includeDetails: boolean): string {
  const p = buildFilterParams(filter);
  p.set("format", format);
  if (includeDetails) p.set("includeDetails", "true");
  return `/admin/api/v1/logs/communication/export?${p.toString()}`;
}

/** Hlavičky/těla přicházejí jako JSON string i jako objekt — sjednotí. */
export function tryParseJson(v: unknown): unknown {
  if (v == null) return null;
  if (typeof v !== "string") return v;
  try {
    return JSON.parse(v);
  } catch {
    return v;
  }
}

function headerEntries(v: unknown): [string, string][] {
  const h = tryParseJson(v);
  return h && typeof h === "object" ? Object.entries(h as Record<string, unknown>).map(([k, x]) => [k, String(x)]) : [];
}

function bodyString(v: unknown): string | null {
  const b = tryParseJson(v);
  if (b == null || b === "") return null;
  return typeof b === "object" ? JSON.stringify(b) : String(b);
}

/* ------------------------- copy buildery (1:1 z Thymeleaf) ------------------------- */

export function toCurlBash(d: CommLogDetail): string {
  const parts = [`curl -X ${d.method ?? "GET"} '${d.endpoint ?? ""}'`];
  for (const [k, v] of headerEntries(d.requestHeaders)) parts.push(` \\\n  -H '${k}: ${v}'`);
  const body = bodyString(d.requestData);
  if (body) parts.push(` \\\n  -d '${body.replace(/'/g, "'\\''")}'`);
  return parts.join("");
}

export function toCurlCmd(d: CommLogDetail): string {
  const parts = [`curl -X ${d.method ?? "GET"} "${d.endpoint ?? ""}"`];
  for (const [k, v] of headerEntries(d.requestHeaders)) parts.push(` ^\n  -H "${k}: ${v.replace(/"/g, '\\"')}"`);
  const body = bodyString(d.requestData);
  if (body) parts.push(` ^\n  -d "${body.replace(/"/g, '\\"')}"`);
  return parts.join("");
}

export function toPowerShell(d: CommLogDetail): string {
  const lines: string[] = [];
  const headers = headerEntries(d.requestHeaders);
  if (headers.length) {
    lines.push("$headers = @{");
    for (const [k, v] of headers) lines.push(`    '${k}' = '${v.replace(/'/g, "''")}'`);
    lines.push("}", "");
  }
  const body = bodyString(d.requestData);
  if (body) lines.push(`$body = '${body.replace(/'/g, "''")}'`, "");
  let cmd = `Invoke-WebRequest -Method ${d.method ?? "GET"} -Uri '${d.endpoint ?? ""}'`;
  if (headers.length) cmd += " -Headers $headers";
  if (body) cmd += ' -Body $body -ContentType $headers["content-type"]';
  lines.push(cmd);
  return lines.join("\n");
}

export function toFetch(d: CommLogDetail): string {
  const headers = headerEntries(d.requestHeaders);
  const body = bodyString(d.requestData);
  const opts: Record<string, unknown> = { method: d.method ?? "GET" };
  if (headers.length) opts.headers = Object.fromEntries(headers);
  if (body) opts.body = body;
  return `fetch('${d.endpoint ?? ""}', ${JSON.stringify(opts, null, 2)});`;
}

export function headersText(v: unknown): string {
  const entries = headerEntries(v);
  return entries.length ? entries.map(([k, val]) => `${k}: ${val}`).join("\n") : "N/A";
}

export function toAllJson(d: CommLogDetail): string {
  return JSON.stringify(
    {
      metadata: {
        timestamp: d.timestamp,
        authServerType: d.authServerType,
        method: d.method,
        endpoint: d.endpoint,
        statusCode: d.statusCode,
        durationMs: d.durationMs,
        tokenId: d.tokenId,
        traceId: d.traceId,
        testTraceId: d.testTraceId,
        errorMessage: d.errorMessage,
      },
      requestHeaders: tryParseJson(d.requestHeaders),
      requestBody: tryParseJson(d.requestData),
      responseHeaders: tryParseJson(d.responseHeaders),
      responseBody: tryParseJson(d.responseData),
    },
    null,
    2,
  );
}

/** Kopírování do schránky s fallbackem pro ne-secure kontext (interní HTTP). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallthrough */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * Položka aplikačního logu. Čas je epoch millis (ne hotový řetězec), aby se dal
 * vykreslit v zóně prohlížeče — server běží v UTC. Úroveň je vlastní pole, takže
 * se řádek obarví přesně, bez hádání regexem nad textem.
 */
export interface AppLogEntry {
  timestamp: number;
  level: string;
  thread: string;
  logger: string;
  message: string;
}

interface AppLogResponse {
  success: boolean;
  entries: AppLogEntry[];
  lineCount: number;
  bufferSize: number;
}

export function useAppLog(lines = 300, level?: string, filter?: string, autoRefreshMs = 0) {
  const p = new URLSearchParams({ lines: String(lines) });
  if (level) p.set("level", level);
  if (filter) p.set("filter", filter);
  return useQuery({
    queryKey: ["app-log", p.toString()],
    queryFn: () => apiFetch<AppLogResponse>(`/admin/api/v1/logs/application?${p.toString()}`),
    // Totéž co u komunikačních logů — aplikační log je ring buffer, který se mění
    // pod rukama; zastaralý snímek je tu stejně zavádějící.
    staleTime: 0,
    refetchInterval: autoRefreshMs > 0 ? autoRefreshMs : false,
  });
}

export function appLogDownloadUrl(): string {
  return "/admin/api/v1/logs/application/download";
}

/** Barva UUI Badge podle HTTP status kódu. */
export function statusColor(code: number | null): "success" | "blue" | "warning" | "error" | "gray" {
  if (code == null) return "error";
  if (code >= 500) return "error";
  if (code >= 400) return "warning";
  if (code >= 300) return "blue";
  if (code >= 200) return "success";
  return "gray";
}
